/* ============================================================
   FREEPET Contact - page-scoped script
   Target: assets/freepet-contact.js
   Responsibilities:
     1. Nav active state (Contact entry)
     2. Contact video: click play (Shopify native + external iframe)
     3. Contact video: viewport preload for Shopify native only
     4. Contact form validation (required + email format + consent)
     5. FAQ accordion (single-open + ARIA sync)
     6. Newsletter subscription validation
   Depends on: window.FP (freepet-base.js: $ / $$ / toast)
   Contract: SKILL-Core Section 5, SKILL-Engineering Section 1
   Stage B:
     - R3: FP.$ / FP.$$ fallback (defends against missing base.js)
     - C-5: contact form preventDefault removed on valid submit
     - C-6: newsletter preventDefault removed on valid submit
     - C-9 + conflict 2: external video via [data-type="external"] + iframe
     - C-9 + conflict 2: initViewportVideo for Shopify native video only
     - P2-1: MAP['contact'] = '/pages/contact'
   ============================================================ */

(function(){
'use strict';

var FP = window.FP = window.FP || {};

/* R3 fallback - ensure FP.$ and FP.$$ exist even if freepet-base.js not loaded yet */
if(typeof FP.$ !== 'function'){
  FP.$ = function(sel, root){ return (root || document).querySelector(sel); };
}
if(typeof FP.$$ !== 'function'){
  FP.$$ = function(sel, root){ return Array.prototype.slice.call((root || document).querySelectorAll(sel)); };
}

/* ============================================================
   1 - Nav active state (Contact entry)
   ============================================================ */
function initNavActive(){
  var page = document.documentElement.getAttribute('data-page');
  if(!page) return;

  var MAP = {
    'contact': '/pages/contact'
  };
  var targetHref = MAP[page];
  if(!targetHref) return;

  requestAnimationFrame(function(){
    var links = FP.$$('.fp-nav-link, .fp-mmenu-link');
    links.forEach(function(a){
      if(a.getAttribute('href') === targetHref){
        a.classList.add('is-active');
        a.setAttribute('aria-current', 'page');
      }
    });
  });
}

/* ============================================================
   2 - External video URL parser (YouTube/Vimeo)
   Returns embed URL or null if unknown.
   ============================================================ */
function getExternalEmbedUrl(url){
  if(!url) return null;
  var u = url.trim();

  var ytMatch = u.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/);
  if(ytMatch && ytMatch[1]){
    return 'https://www.youtube.com/embed/' + ytMatch[1] + '?rel=0&modestbranding=1';
  }

  var vmMatch = u.match(/(?:vimeo\.com\/|player\.vimeo\.com\/video\/)(\d+)/);
  if(vmMatch && vmMatch[1]){
    return 'https://player.vimeo.com/video/' + vmMatch[1] + '?title=0&byline=0&portrait=0';
  }

  return null;
}

/* ============================================================
   3 - Inject external iframe replacing placeholder div
   ============================================================ */
function injectExternalIframe(container, embedUrl){
  var iframe = document.createElement('iframe');
  iframe.className = 'fp-contact-video-el';
  iframe.src = embedUrl;
  iframe.title = 'Contact team greeting video';
  iframe.loading = 'lazy';
  iframe.referrerPolicy = 'strict-origin-when-cross-origin';
  iframe.setAttribute('allow', 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share');
  iframe.setAttribute('allowfullscreen', '');
  iframe.setAttribute('frameborder', '0');
  container.parentNode.replaceChild(iframe, container);
  return iframe;
}

/* ============================================================
   4 - Contact Video (click play: Shopify native + external iframe)
   ============================================================ */
function initContactVideo(){
  var root = FP.$('[data-section="contact-video"]');
  if(!root) return;

  var card = root.querySelector('.fp-contact-video-inner');
  var btn = root.querySelector('.fp-contact-video-btn');
  if(!card || !btn) return;

  var external = root.querySelector('[data-type="external"]');
  var video = root.querySelector('.fp-contact-video-el');

  btn.addEventListener('click', function(){
    if(external){
      var extSrc = external.getAttribute('data-src');
      if(!extSrc){
        showComingSoon(btn);
        return;
      }
      var embedUrl = getExternalEmbedUrl(extSrc);
      if(!embedUrl){
        showComingSoon(btn);
        return;
      }
      card.classList.add('is-playing');
      injectExternalIframe(external, embedUrl);
      return;
    }

    if(!video) return;
    var src = video.getAttribute('data-src');
    if(!src){
      showComingSoon(btn);
      return;
    }
    if(!video.getAttribute('src')){ video.setAttribute('src', src); video.load(); }
    card.classList.add('is-playing');
    video.muted = false;
    var p = video.play(); if(p && p.catch) p.catch(function(){});
  });

  function showComingSoon(btn){
    var label = btn.querySelector('span');
    if(label && !btn.dataset.busy){
      btn.dataset.busy = '1';
      if(btn._busyTimer){ clearTimeout(btn._busyTimer); }
      var original = label.textContent;
      label.textContent = 'Coming soon';
      btn._busyTimer = setTimeout(function(){
        label.textContent = original;
        delete btn.dataset.busy;
        delete btn._busyTimer;
      }, 1800);
    }
  }

  if(video){
    video.addEventListener('ended', function(){ card.classList.remove('is-playing'); video.currentTime = 0; });
    video.addEventListener('error', function(){ video.style.display='none'; card.classList.remove('is-playing'); }, { passive:true });

    if('IntersectionObserver' in window){
      var io = new IntersectionObserver(function(en){
        if(!en[0].isIntersecting && !video.paused) video.pause();
      }, { threshold:0.15 });
      io.observe(root);
    }
    document.addEventListener('visibilitychange', function(){
      if(document.hidden && !video.paused) video.pause();
    });
  }
}

/* ============================================================
   5 - Viewport preload for Shopify native video only
   - Native: <video data-src> without data-type="external"
   - Triggers at 25% viewport: set src + load (no auto-play)
   - External iframe: NOT handled here (click-triggered in initContactVideo)
   ============================================================ */
function initViewportVideo(){
  var natives = FP.$$('[data-section="contact-video"] video[data-src]:not([data-type="external"])');
  if(!natives.length) return;

  if(!('IntersectionObserver' in window)){
    natives.forEach(function(v){
      if(!v.getAttribute('src')){
        var s = v.getAttribute('data-src');
        if(s){ v.setAttribute('src', s); v.load(); }
      }
    });
    return;
  }

  var io = new IntersectionObserver(function(entries){
    entries.forEach(function(entry){
      if(!entry.isIntersecting) return;
      var v = entry.target;
      if(!v.getAttribute('src')){
        var s = v.getAttribute('data-src');
        if(s){ v.setAttribute('src', s); v.load(); }
      }
      io.unobserve(v);
    });
  }, { threshold: 0.25 });

  natives.forEach(function(v){ io.observe(v); });
}

/* ============================================================
   6 - Contact form validation
   On valid submit: allow real submit to Shopify {% form 'contact' %}
   On invalid submit: preventDefault + show message + focus first error
   ============================================================ */
var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function initContactForm(){
  var root = FP.$('[data-section="contact-form"]');
  if(!root) return;

  var form = root.querySelector('[data-contact-form]');
  if(!form) return;
  var msg = root.querySelector('[data-contact-form-msg]');

  function setMsg(text, isError){
    if(!msg) return;
    msg.textContent = text;
    msg.className = 'fp-form-msg ' + (isError ? 'fp-form-msg-error' : 'fp-form-msg-success');
  }

  form.addEventListener('submit', function(e){
    var name = form.querySelector('[name="contact[name]"], [name="name"]');
    var email = form.querySelector('[name="contact[email]"], [name="email"]');
    var topic = form.querySelector('[name="contact[topic]"], [name="topic"]');
    var message = form.querySelector('[name="contact[body]"], [name="message"]');
    var consent = form.querySelector('[name="contact[consent]"], [name="consent"]');

    var missing = [];
    var firstKey = '';

    if(!name || !name.value.trim()){ missing.push('full name'); firstKey = firstKey || 'name'; }
    if(!email || !email.value.trim()){ missing.push('email'); firstKey = firstKey || 'email'; }
    if(!topic || !topic.value){ missing.push('topic'); firstKey = firstKey || 'topic'; }
    if(!message || !message.value.trim() || message.value.trim().length < 10){ missing.push('message (at least 10 characters)'); firstKey = firstKey || 'message'; }

    if(missing.length){
      e.preventDefault();
      setMsg('Please fill in: ' + missing.join(', ') + '.', true);
      var firstEl = form.querySelector('[name="' + firstKey + '"]');
      if(firstEl) firstEl.focus();
      return;
    }

    if(!EMAIL_RE.test(email.value.trim())){
      e.preventDefault();
      setMsg('Please enter a valid email address.', true);
      email.focus();
      return;
    }

    if(consent && !consent.checked){
      e.preventDefault();
      setMsg('Please agree to the Privacy Policy before sending.', true);
      consent.focus();
      return;
    }

    /* Valid - allow submit to Shopify backend. Shopify handles success via form.posted_successfully? (File 7 Liquid) */
  });

  form.addEventListener('input', function(){
    if(msg && msg.classList.contains('fp-form-msg-error')){
      msg.textContent = '';
      msg.className = 'fp-form-msg';
    }
  });
}

/* ============================================================
   7 - FAQ accordion
   ============================================================ */
function initContactFAQ(){
  var root = FP.$('[data-section="contact-faq"]');
  if(!root) return;

  var items = FP.$$('.fp-contact-faq-item', root);

  items.forEach(function(item){
    var q = item.querySelector('.fp-contact-faq-q');
    if(!q) return;

    q.addEventListener('click', function(){
      var wasOpen = item.classList.contains('is-open');
      items.forEach(function(s){
        s.classList.remove('is-open');
        var b = s.querySelector('.fp-contact-faq-q');
        if(b) b.setAttribute('aria-expanded', 'false');
      });
      if(!wasOpen){
        item.classList.add('is-open');
        q.setAttribute('aria-expanded', 'true');
      }
    });
  });
}

/* ============================================================
   8 - Newsletter subscription validation
   On valid submit: allow real submit to Shopify {% form 'customer' %}
   On invalid submit: preventDefault + show message + focus input
   ============================================================ */
function initContactNewsletter(){
  var r = FP.$('[data-section="contact-newsletter"]');
  if(!r) return;
  var form = r.querySelector('form');
  if(!form) return;
  var msg = r.querySelector('.fp-form-msg');
  var input = form.querySelector('input[type="email"]');

  form.addEventListener('submit', function(e){
    var v = input ? input.value.trim() : '';
    var ok = EMAIL_RE.test(v);
    if(!ok){
      e.preventDefault();
      if(msg){
        msg.textContent = 'Please enter a valid email address.';
        msg.className = 'fp-form-msg fp-form-msg-error';
      }
      if(input) input.focus();
      return;
    }
    /* Valid - allow submit to Shopify backend. Shopify handles success via form.posted_successfully? (File 11 Liquid) */
  });
}

/* ============================================================
   Boot
   ============================================================ */
function boot(){
  initNavActive();
  initContactVideo();
  initViewportVideo();
  initContactForm();
  initContactFAQ();
  initContactNewsletter();
}

if(document.readyState === 'loading'){
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}

})();